import { SuppliesShippingRateStep } from "@redotech/redo-flows-common/advanced-flow-db-parser";
import {
  DEFAULT_SUPPLIES_SHIPPING_LABEL,
  SuppliesShippingRateMode,
} from "@redotech/redo-flows-core-schema-common/common";
import { useTranslation } from "@redotech/redo-i18n";
import TruckIcon from "@redotech/redo-icons/truck-01.svg";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { Handle, Position } from "@xyflow/react";
import { memo } from "react";
import { PartialWithRequiredType } from "../steps";
import { toSuppliesRate } from "../steps/supplies/supplies-shipping-step-values";
import { BaseNode } from "./base-node";
import * as NodeCSS from "./node.module.css";

export interface SuppliesShippingRateNodeData {
  step: PartialWithRequiredType<SuppliesShippingRateStep>;
  deleteElements: (id: string) => void;
  readonly: boolean;
}

export const SuppliesShippingRateNode = memo(function SuppliesShippingRateNode({
  id,
  data,
  selected,
}: {
  id: string;
  data: SuppliesShippingRateNodeData;
  selected?: boolean | undefined;
}) {
  const { t } = useTranslation();
  const { label } = data.step;
  const rate = toSuppliesRate(data.step.rate);
  const amountText = !rate
    ? null
    : rate.mode === SuppliesShippingRateMode.PERCENT_OF_CART
      ? t("{{percent}}% of cart", { percent: rate.percentBasisPoints / 100 })
      : rate.amountMinorUnits === 0
        ? t("Free")
        : `$${(rate.amountMinorUnits / 100).toFixed(2)}`;
  return (
    <BaseNode
      icon={<RedoFeaturedIcon color="success" Icon={TruckIcon} size="sm" />}
      onDelete={data.readonly ? undefined : () => data.deleteElements(id)}
      selected={selected}
      step={data.step}
      title={t("Set shipping cost")}
    >
      <Handle id="top" position={Position.Top} type="target" />
      <Flex className={NodeCSS.fullWidth} dir="column" gap="xs">
        {amountText == null ? (
          <Text className={NodeCSS.placeholderText}>{t("No amount set")}</Text>
        ) : (
          <Text fontSize="sm" fontWeight="semibold">
            {amountText}
          </Text>
        )}
        <Flex w="full">
          <RedoBadge
            color="gray"
            size="xs"
            text={label || DEFAULT_SUPPLIES_SHIPPING_LABEL}
          />
        </Flex>
      </Flex>
    </BaseNode>
  );
});
